// Import all service categories
import consultationsServices from './consultations'
import bloodTestsServices from './blood-tests'
import diagnosticsServices from './diagnostics'
import vaccinationsServices from './vaccinations'
import minorProceduresServices from './minor-procedures'
import ivTherapyServices from './iv-therapy'
import healthWellnessScreeningServices from './health-wellness-screening'
import therapiesServices from './therapies'
import medicationAdministrationServices from './medication-administration'
import homeOnsiteVisitsServices from './home-onsite-visits'
import administrativeServices from './administrative-services'
import telehealthVirtualCareServices from './telehealth-virtual-care'
import specialistServices from './specialist-services'

// Aggregate all services
export const allServices = [
  ...consultationsServices,
  ...bloodTestsServices,
  ...diagnosticsServices,
  ...vaccinationsServices,
  ...minorProceduresServices,
  ...ivTherapyServices,
  ...healthWellnessScreeningServices,
  ...therapiesServices,
  ...medicationAdministrationServices,
  ...homeOnsiteVisitsServices,
  ...administrativeServices,
  ...telehealthVirtualCareServices,
  ...specialistServices
]

// Aggregate all services (alternative export name for compatibility)
export const ALL_SERVICES = allServices

// Category metadata with emojis and colors
export const categoryMetadata = {
  Consultations: {
    emoji: '🩺',
    color: '#1890ff',
    description: 'Medical consultations and examinations'
  },
  'Blood Tests': {
    emoji: '🔬',
    color: '#722ed1',
    description: 'Laboratory blood testing services'
  },
  Diagnostics: {
    emoji: '📊',
    color: '#13c2c2',
    description: 'Diagnostic procedures and imaging'
  },
  'Vaccinations & Immunisations': {
    emoji: '💉',
    color: '#52c41a',
    description: 'Vaccination and immunization services'
  },
  'Minor Procedures': {
    emoji: '🔧',
    color: '#fa541c',
    description: 'Minor medical procedures and treatments'
  },
  'IV Therapy': {
    emoji: '💧',
    color: '#1890ff',
    description: 'Intravenous therapy and infusions'
  },
  'Health & Wellness Screening': {
    emoji: '🏥',
    color: '#eb2f96',
    description: 'Preventive health screenings and assessments'
  },
  Therapies: {
    emoji: '🧘',
    color: '#722ed1',
    description: 'Physical and mental health therapies'
  },
  'Medication Administration': {
    emoji: '💊',
    color: '#f5222d',
    description: 'Medication delivery and management services'
  },
  'Home & Onsite Visits': {
    emoji: '🏠',
    color: '#faad14',
    description: 'Mobile and on-site healthcare services'
  },
  'Administrative Services': {
    emoji: '📋',
    color: '#52c41a',
    description: 'Medical documentation and administrative tasks'
  },
  'Telehealth / Virtual Care': {
    emoji: '💻',
    color: '#13c2c2',
    description: 'Remote healthcare and virtual consultations'
  },
  'Specialist Services': {
    emoji: '👨‍⚕️',
    color: '#fa541c',
    description: 'Specialized medical consultations and treatments'
  }
}

// Category metadata (alternative export name for compatibility)
export const CATEGORY_METADATA = categoryMetadata

// Services organized by category for easy access
export const servicesByCategory = {
  Consultations: consultationsServices,
  'Blood Tests': bloodTestsServices,
  Diagnostics: diagnosticsServices,
  'Vaccinations & Immunisations': vaccinationsServices,
  'Minor Procedures': minorProceduresServices,
  'IV Therapy': ivTherapyServices,
  'Health & Wellness Screening': healthWellnessScreeningServices,
  Therapies: therapiesServices,
  'Medication Administration': medicationAdministrationServices,
  'Home & Onsite Visits': homeOnsiteVisitsServices,
  'Administrative Services': administrativeServices,
  'Telehealth / Virtual Care': telehealthVirtualCareServices,
  'Specialist Services': specialistServices
}

// Services by category (alternative export name for compatibility)
export const SERVICES_BY_CATEGORY = servicesByCategory

// Get services by category
export const getServicesByCategory = (categoryName) => {
  return servicesByCategory[categoryName] || []
}

// Get all categories
export const getAllCategories = () => {
  return Object.keys(categoryMetadata)
}

// Get all categories (alternative export name for compatibility)
export const getCategories = getAllCategories

// Search functionality - now supports optional services parameter
export const searchServices = (query, services = allServices) => {
  if (!query || query.trim() === '') return services

  const searchTerm = query.toLowerCase().trim()

  return services.filter((service) => {
    const searchableText = [
      service.serviceName,
      service.category,
      service.subCategory,
      service.description,
      service.specialty,
      ...(service.tags || [])
    ]
      .join(' ')
      .toLowerCase()

    return searchableText.includes(searchTerm)
  })
}

// Filter services by multiple criteria
export const filterServices = (filters, services = allServices) => {
  let filteredServices = [...services]

  if (filters.categories && filters.categories.length > 0) {
    filteredServices = filteredServices.filter((service) => filters.categories.includes(service.category))
  }

  if (filters.deliveryModes && filters.deliveryModes.length > 0) {
    filteredServices = filteredServices.filter((service) =>
      service.deliveryModes.some((mode) => filters.deliveryModes.includes(mode))
    )
  }

  if (filters.practitionerTypes && filters.practitionerTypes.length > 0) {
    filteredServices = filteredServices.filter((service) =>
      filters.practitionerTypes.includes(service.requiredPractitionerType)
    )
  }

  if (filters.costRange) {
    const { min, max } = filters.costRange
    filteredServices = filteredServices.filter((service) => service.cost >= min && service.cost <= max)
  }

  return filteredServices
}

// Get services by delivery mode
export const getServicesByDeliveryMode = (deliveryMode) => {
  return allServices.filter((service) => service.deliveryModes.includes(deliveryMode))
}

// Get services by practitioner type
export const getServicesByPractitioner = (practitionerType) => {
  return allServices.filter((service) => service.requiredPractitionerType === practitionerType)
}

// Get service by ID
export const getServiceById = (serviceId) => {
  return allServices.find((service) => service.serviceId === serviceId) || null
}

// Get category metadata
export const getCategoryMetadata = (category) => {
  return categoryMetadata[category] || {}
}

// ZAR currency formatter
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

// Format duration for display
export const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes} min`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (remainingMinutes === 0) {
    return `${hours}h`
  }
  return `${hours}h ${remainingMinutes}min`
}

// Export individual category services for specific use
export {
  consultationsServices,
  bloodTestsServices,
  diagnosticsServices,
  vaccinationsServices,
  minorProceduresServices,
  ivTherapyServices,
  healthWellnessScreeningServices,
  therapiesServices,
  medicationAdministrationServices,
  homeOnsiteVisitsServices,
  administrativeServices,
  telehealthVirtualCareServices,
  specialistServices
}

// Default export for legacy compatibility
export default {
  allServices,
  ALL_SERVICES,
  categoryMetadata,
  CATEGORY_METADATA,
  servicesByCategory,
  SERVICES_BY_CATEGORY,
  getServicesByCategory,
  getAllCategories,
  getCategories,
  searchServices,
  filterServices,
  getServicesByDeliveryMode,
  getServicesByPractitioner,
  getServiceById,
  getCategoryMetadata,
  formatCurrency,
  formatDuration
}
